import React, { useEffect, useReducer, useRef, useCallback, useMemo } from 'react';
import { withCookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Spinner, Alert } from 'react-bootstrap';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Play } from 'lucide-react';

// local helpers & services
import { BRAND, decode, getUID, isMobile, sendNotfication } from '../../../../Helper';
import socket from '../../../../Socket';
import storage from '../../../../Storage';
import { gamesService } from '../../../../utils/Services';
import { preloadImages } from '../../../hooks/useImageOptimization';
import GameSection from '../../../Components/GameSection/GameSection';
import GameProviders from './Provider';
import SkylaSlider from '../../../Components/SkylaBannerSlider';
import Banner from '../../../Components/Banner';
import PaymentBar from '../../../Components/PaymentBar';
import MobilePaymentBR from '../../../Components/MobilePaymetBar';
import CasinoLeaderboard from '../../../Components/HelloGamesTable';
import Esports from '../../../Components/Esports';
import SportsNavFlipper from '../../../Components/SportsNavFlipper/SportsNavFlipper';
import SportsBanner from '../../../Components/Sportbanner';

// =============== Styled Components =============== //
const FullScreenContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  background-color: #1b2336;
`;

const FullScreenIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const BackButton = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  background: none;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TopTitle = styled.div`
  color: white;
  font-size: 16px;
  font-weight: bold;
  margin-left: 4px;
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 10px;
`;

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 11px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const GameCard = styled.div`
  min-width: 142px;
  height: 195px;
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-5px);
  }
  @media (max-width: 768px) {
    min-width: 115px;
    height: 160px;
  }
`;

const GameOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  ${GameCard}:hover & {
    opacity: 1;
  }
`;

const GameImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const PlayIconContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PlayIcon = styled(Play)`
  color: #fff;
  font-size: 24px;
`;

// =============== Functional Component =============== //
const DEFAULT_PAGE_SIZE = 20;

// Provider sections in display order. Each is rendered as its own section and
// fetched per vendor with game_type=Slot (only the vendor changes).
// NOTE: vendor names are CASE-SENSITIVE. These are the ONLY vendors that return
// game_type=Slot games (verified against the live API). Add a vendor here only if
// it actually has Slot-type games, otherwise its section renders empty.
const vendorList = [
  'CQ9', 'Playtech', 'hacksaw', 'PgsGaming', 'Rich88', 'BNG',
];

// Pretty section titles per vendor key (falls back to capitalized vendor name)
const vendorTitles = {
  CQ9: 'CQ9 Gaming',
  Playtech: 'Playtech',
  hacksaw: 'Hacksaw',
  PgsGaming: 'PgsGaming',
  Rich88: 'Rich88',
  BNG: 'BNG',
};

const titleFor = (vendor) => vendorTitles[vendor] || (vendor.charAt(0).toUpperCase() + vendor.slice(1));

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING': return { ...state, loading: action.payload };
    case 'SET_IFRAME': return { ...state, showIframe: action.payload.show, iframeUrl: action.payload.url, iframeLoading: action.payload.loading };
    case 'SET_VENDOR_DATA': return { ...state, vendors: { ...state.vendors, ...action.payload } };
    case 'SET_PAGINATION': return { ...state, vendorPagination: { ...state.vendorPagination, ...action.payload } };
    case 'SET_SLOTS': return { ...state, slots: action.payload };
    case 'SET_MESSAGE': return { ...state, message: action.payload };
    case 'SET_IS_LOGGED': return { ...state, isLogged: action.payload };
    case 'SET_GAMES_LOADING': return { ...state, gamesLoading: action.payload };
    default: return state;
  }
}

const initialState = {
  loading: false,
  message: null,
  isLogged: !!localStorage.getItem('logged'),
  showIframe: false,
  iframeUrl: '',
  iframeLoading: true,
  vendors: {},
  vendorPagination: {},
  slots: [],
  gamesLoading: true
};

function HomeFunctional(props) {
  const finalState = useSelector((s) => s.finalState.finalState);
  const [state, dispatch] = useReducer(reducer, initialState);
  const scrollRefs = useRef({});

  vendorList.forEach(v => { if (!scrollRefs.current[v]) scrollRefs.current[v] = React.createRef(); });
  if (!scrollRefs.current['slot']) scrollRefs.current['slot'] = React.createRef();

  // caching helpers: use sessionStorage to avoid re-fetch within session
  const getCachedVendor = (vendor) => {
    try {
      const raw = sessionStorage.getItem(`vendor_cache_${vendor}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // cache TTL 10 minutes
      if (Date.now() - parsed._ts > 10 * 60 * 1000) { sessionStorage.removeItem(`vendor_cache_${vendor}`); return null; }
      return parsed.data;
    } catch (e) { return null; }
  };
  const setCachedVendor = (vendor, data) => {
    try { sessionStorage.setItem(`vendor_cache_${vendor}`, JSON.stringify({ _ts: Date.now(), data })); } catch (e) { }
  };

  const fetchVendor = useCallback(async (vendor, page = 1, perPage = DEFAULT_PAGE_SIZE) => {
    // check session cache first
    const cached = getCachedVendor(vendor);
    if (cached) return { games: cached.games || [], pagination: cached.pagination || { current_page: page, total_pages: 1 } };

    try {
      // Always keep game_type=Slot, only the vendor changes:
      // GET /jsGames/games?game_type=Slot&vendor=<vendor>&page=<page>&per_page=<perPage>
      const result = await gamesService.getSlotGamesByVendor(vendor, page, perPage);
      if (result.success && result.payload?.games) {
        const value = { games: result.payload.games, pagination: result.payload.pagination || { current_page: page, total_pages: 1 } };
        // cache the response
        setCachedVendor(vendor, value);
        // preload a few images for this vendor only
        const imgs = (value.games || []).slice(0, 30).map(g => g.game_icon).filter(Boolean);
        if (imgs.length) preloadImages(imgs, 4, 'normal');
        return value;
      }
      return { games: [], pagination: { current_page: page, total_pages: 1 } };
    } catch (e) {
      console.error('Error fetching', vendor, e);
      return { games: [], pagination: { current_page: 1, total_pages: 1 } };
    }
  }, []);

  // Fetch Slot games per vendor (section-wise). For each vendor we call:
  // GET https://api.ibitplay.com/jsGames/games?game_type=Slot&vendor=<vendor>&page=1&per_page=20
  // State updates incrementally so each provider section renders as soon as it arrives.
  useEffect(() => {
    let mounted = true;
    (async () => {
      dispatch({ type: 'SET_GAMES_LOADING', payload: true });

      for (const vendor of vendorList) {
        if (!mounted) break;
        try {
          const res = await fetchVendor(vendor, 1);
          if (!mounted) break;
          dispatch({ type: 'SET_VENDOR_DATA', payload: { [vendor]: res.games } });
          dispatch({
            type: 'SET_PAGINATION',
            payload: { [vendor]: { currentPage: res.pagination.current_page, totalPages: res.pagination.total_pages, loading: false } }
          });
        } catch (err) {
          if (!mounted) break;
          dispatch({ type: 'SET_VENDOR_DATA', payload: { [vendor]: [] } });
        }
      }

      if (mounted) dispatch({ type: 'SET_GAMES_LOADING', payload: false });
    })();

    return () => { mounted = false; };
  }, [fetchVendor]);

  const handleGameLaunch = useCallback(async (game) => {
    const isLogged = localStorage.getItem('logged') === 'true';
    if (!isLogged) { sendNotfication('Please login to play', 'error'); return; }
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const coins = localStorage.getItem('coin') || 'USDT';
      if (!['USDT', 'INR', 'BDT'].includes(coins)) {
        sendNotfication('Please Swap Into USDT To Play', 'error'); return;
      }
      const payload = { game_uid: game.game_uid, user_id: getUID(), credit_amount: 100, currency_code: coins, language: 'en' };
      const result = await gamesService.launchGame(payload);
      if (result.success && result.data?.game_launch_url) {
        dispatch({ type: 'SET_IFRAME', payload: { show: true, url: result.data.game_launch_url, loading: true } });
      } else sendNotfication('Game under maintenance', 'error');
    } catch (e) {
      dispatch({ type: 'SET_MESSAGE', payload: { type: 'error', text: e.message || 'Failed to launch game' } });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
      setTimeout(() => dispatch({ type: 'SET_MESSAGE', payload: null }), 5000);
    }
  }, []);

  const closeIframe = useCallback(() => dispatch({ type: 'SET_IFRAME', payload: { show: false, url: '', loading: true } }), []);

  const scrollLeft = (vendor) => scrollRefs.current[vendor]?.current?.scrollBy({ left: -400, behavior: 'smooth' });
  const scrollRight = (vendor) => scrollRefs.current[vendor]?.current?.scrollBy({ left: 400, behavior: 'smooth' });

  // Render vendors with Esports only once between the first two groups of three providers
  const renderVendorsWithEsports = () => {
    const nodes = [];
    // Prepare vendors excluding any 'esports' key
    const filtered = vendorList.filter(v => v !== 'esports');

    const firstThree = filtered.slice(0, 3);
    const nextThree = filtered.slice(3, 6);
    const rest = filtered.slice(6);

    // Render first 3 providers
    firstThree.forEach(vendor => {
      nodes.push(
        <div key={vendor} style={{ marginTop: 12 }}>
          <GameSection
            title={titleFor(vendor)}
            games={state.vendors[vendor] || []}
            onGameClick={handleGameLaunch}
            vendor={vendor}
            vendorPagination={state.vendorPagination[vendor]}
            onPagination={() => { }}
            enabled={true}
            showMoreState={false}
            onToggleShowMore={() => { }}
            scrollContainerRef={scrollRefs.current[vendor]}
            onScrollLeft={() => scrollLeft(vendor)}
            onScrollRight={() => scrollRight(vendor)}
            isMobileView={isMobile()}
            loading={state.gamesLoading}
          />
        </div>
      );
    });

    // Insert Esports after the first 3 providers
    // nodes.push(
    //   <div key="esports-insert" style={{ marginTop: 12 }}>
    //     <Esports />
    //   </div>
    // );

    // Render next 3 providers
    nextThree.forEach(vendor => {
      nodes.push(
        <div key={vendor} style={{ marginTop: 12 }}>
          <GameSection
            title={titleFor(vendor)}
            games={state.vendors[vendor] || []}
            onGameClick={handleGameLaunch}
            vendor={vendor}
            vendorPagination={state.vendorPagination[vendor]}
            onPagination={() => { }}
            enabled={true}
            showMoreState={false}
            onToggleShowMore={() => { }}
            scrollContainerRef={scrollRefs.current[vendor]}
            onScrollLeft={() => scrollLeft(vendor)}
            onScrollRight={() => scrollRight(vendor)}
            isMobileView={isMobile()}
            loading={state.gamesLoading}
          />
        </div>
      );
    });

    // Render the rest of providers (all remaining)
    rest.forEach(vendor => {
      nodes.push(
        <div key={vendor} style={{ marginTop: 12 }}>
          <GameSection
            title={titleFor(vendor)}
            games={state.vendors[vendor] || []}
            onGameClick={handleGameLaunch}
            vendor={vendor}
            vendorPagination={state.vendorPagination[vendor]}
            onPagination={() => { }}
            enabled={true}
            showMoreState={false}
            onToggleShowMore={() => { }}
            scrollContainerRef={scrollRefs.current[vendor]}
            onScrollLeft={() => scrollLeft(vendor)}
            onScrollRight={() => scrollRight(vendor)}
            isMobileView={isMobile()}
            loading={state.gamesLoading}
          />
        </div>
      );
    });

    return nodes;
  };

  if (state.showIframe) {
    return (
      <FullScreenContainer>
        <FullScreenIframe src={state.iframeUrl} title="Game Iframe" />
        <BackButton onClick={closeIframe}><FontAwesomeIcon icon={faArrowLeft} /></BackButton>
      </FullScreenContainer>
    );
  }

  return (
    <>
      <Helmet>
        <title>{BRAND} - Crypto Casino Games and Crypto Gambling</title>
      </Helmet>

      {state.isLogged ? <SkylaSlider /> : <Banner />}
      {/* <SportsNavFlipper /> */}
     
      {isMobile() ? <MobilePaymentBR /> : <PaymentBar />}

      {/* Slot games, section-wise per provider (fetched per vendor) */}
      {renderVendorsWithEsports()}

      {/* Keep GameProviders at the end if you still want it there */}
     

      <CasinoLeaderboard />

      {state.loading && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 9999 }}>
          <Spinner animation="border" />
        </div>
      )}

      {state.message && (
        <Alert variant={state.message.type === 'error' ? 'danger' : 'success'} style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          {state.message.text}
        </Alert>
      )}
    </>
  );
}

export default withCookies(HomeFunctional);




// import React, { useEffect, useReducer, useRef, useCallback, useMemo } from 'react';
// import { withCookies } from 'react-cookie';
// import { useSelector } from 'react-redux';
// import { Helmet } from 'react-helmet';
// import { Spinner, Alert } from 'react-bootstrap';
// import styled from 'styled-components';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
// import { Play } from 'lucide-react';

// // local helpers & services
// import { BRAND, decode, getUID, isMobile, sendNotfication } from '../../../../Helper';
// import socket from '../../../../Socket';
// import storage from '../../../../Storage';
// import { gamesService } from '../../../../utils/Services';
// import { preloadImages } from '../../../hooks/useImageOptimization';
// import GameSection from '../../../Components/GameSection/GameSection';
// import GameProviders from './Provider';
// import SkylaSlider from '../../../Components/SkylaBannerSlider';
// import Banner from '../../../Components/Banner';
// import PaymentBar from '../../../Components/PaymentBar';
// import MobilePaymentBR from '../../../Components/MobilePaymetBar';
// import CasinoLeaderboard from '../../../Components/HelloGamesTable';
// import Esports from '../../../Components/Esports';
// import SportsNavFlipper from '../../../Components/SportsNavFlipper/SportsNavFlipper';

// // =============== Styled Components =============== //
// const FullScreenContainer = styled.div`
//   position: fixed;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   z-index: 9999;
//   background-color: #1b2336;
// `;

// const FullScreenIframe = styled.iframe`
//   width: 100%;
//   height: 100%;
//   border: none;
// `;

// const BackButton = styled.button`
//   position: absolute;
//   top: 10px;
//   left: 10px;
//   z-index: 1000;
//   background: none;
//   border: none;
//   color: #fff;
//   font-size: 24px;
//   cursor: pointer;
// `;

// const HeaderContainer = styled.div`
//   width: 100%;
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
// `;

// const TopTitle = styled.div`
//   color: white;
//   font-size: 16px;
//   font-weight: bold;
//   margin-left: 4px;
// `;

// const CarouselContainer = styled.div`
//   position: relative;
//   width: 100%;
//   margin-top: 10px;
// `;

// const ScrollContainer = styled.div`
//   display: flex;
//   overflow-x: auto;
//   overflow-y: hidden;
//   gap: 11px;
//   scrollbar-width: none;
//   -ms-overflow-style: none;
//   &::-webkit-scrollbar {
//     display: none;
//   }
// `;

// const GameCard = styled.div`
//   min-width: 142px;
//   height: 195px;
//   border-radius: 8px;
//   position: relative;
//   cursor: pointer;
//   overflow: hidden;
//   transition: transform 0.2s ease;
//   &:hover {
//     transform: translateY(-5px);
//   }
//   @media (max-width: 768px) {
//     min-width: 115px;
//     height: 160px;
//   }
// `;

// const GameOverlay = styled.div`
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   background-color: rgba(0, 0, 0, 0.4);
//   backdrop-filter: blur(5px);
//   border-radius: 8px;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   opacity: 0;
//   transition: opacity 0.2s ease;
//   ${GameCard}:hover & {
//     opacity: 1;
//   }
// `;

// const GameImage = styled.img`
//   width: 100%;
//   height: 100%;
//   object-fit: cover;
//   border-radius: 8px;
// `;

// const PlayIconContainer = styled.div`
//   width: 50px;
//   height: 50px;
//   border-radius: 50%;
//   background-color: rgba(255, 255, 255, 0.3);
//   display: flex;
//   justify-content: center;
//   align-items: center;
// `;

// const PlayIcon = styled(Play)`
//   color: #fff;
//   font-size: 24px;
// `;

// // =============== Functional Component =============== //
// const DEFAULT_PAGE_SIZE = 24;
// const vendorList = [
//   'evolution','pragmatic','spribe','ezugi','ideal','microgaming','pgsoft','hacksaw','jili','netent','pragmaticlive','bgaming','eazygaming','skywind','BigTimeGaming','Playson','Ag','Playtech','BNG','PgsGaming','Rich88','NextSpin','esports','JDBGaming','CQ9','km','Evoplay'
// ];

// function reducer(state, action){
//   switch(action.type){
//     case 'SET_LOADING': return {...state, loading: action.payload};
//     case 'SET_IFRAME': return {...state, showIframe: action.payload.show, iframeUrl: action.payload.url, iframeLoading: action.payload.loading};
//     case 'SET_VENDOR_DATA': return {...state, vendors: {...state.vendors, ...action.payload}};
//     case 'SET_PAGINATION': return {...state, vendorPagination: {...state.vendorPagination, ...action.payload}};
//     case 'SET_MESSAGE': return {...state, message: action.payload};
//     case 'SET_IS_LOGGED': return {...state, isLogged: action.payload};
//     case 'SET_GAMES_LOADING': return {...state, gamesLoading: action.payload};
//     default: return state;
//   }
// }

// const initialState = {
//   loading: false,
//   message: null,
//   isLogged: !!localStorage.getItem('logged'),
//   showIframe: false,
//   iframeUrl: '',
//   iframeLoading: true,
//   vendors: {},
//   vendorPagination: {},
//   gamesLoading: false
// };

// function HomeFunctional(props){
//   const finalState = useSelector((s) => s.finalState.finalState);
//   const [state, dispatch] = useReducer(reducer, initialState);
//   const scrollRefs = useRef({});

//   vendorList.forEach(v => { if(!scrollRefs.current[v]) scrollRefs.current[v] = React.createRef(); });

//   // caching helpers: use sessionStorage to avoid re-fetch within session
//   const getCachedVendor = (vendor) => {
//     try{
//       const raw = sessionStorage.getItem(`vendor_cache_${vendor}`);
//       if(!raw) return null;
//       const parsed = JSON.parse(raw);
//       // cache TTL 10 minutes
//       if(Date.now() - parsed._ts > 10 * 60 * 1000) { sessionStorage.removeItem(`vendor_cache_${vendor}`); return null; }
//       return parsed.data;
//     }catch(e){ return null; }
//   };
//   const setCachedVendor = (vendor, data) => {
//     try{ sessionStorage.setItem(`vendor_cache_${vendor}`, JSON.stringify({ _ts: Date.now(), data })); }catch(e){}
//   };

//   const fetchVendor = useCallback(async (vendor, page = 1, perPage = DEFAULT_PAGE_SIZE) => {
//     // check session cache first
//     const cached = getCachedVendor(vendor);
//     if(cached) return { games: cached.games || [], pagination: cached.pagination || { current_page: page, total_pages: 1 } };

//     try {
//       const token = localStorage.getItem('token');
//       const headers = { Authorization: `Bearer ${token}` };
//       const result = await gamesService.getGamesByVendor(vendor, page, perPage, headers);
//       if(result.success && result.payload?.games){
//         const value = { games: result.payload.games, pagination: result.payload.pagination || { current_page: page, total_pages: 1 } };
//         // cache the response
//         setCachedVendor(vendor, value);
//         // preload a few images for this vendor only
//         const imgs = (value.games || []).slice(0, 30).map(g => g.game_icon).filter(Boolean);
//         if(imgs.length) preloadImages(imgs, 4, 'normal');
//         return value;
//       }
//       return { games: [], pagination: { current_page: page, total_pages: 1 } };
//     } catch(e){
//       console.error('Error fetching', vendor, e);
//       return { games: [], pagination: { current_page: 1, total_pages: 1 } };
//     }
//   }, []);

//   // Sequential fetching: fetch vendors one by one and update state as they arrive
//   useEffect(() => {
//     let mounted = true;
//     (async () => {
//       dispatch({ type: 'SET_GAMES_LOADING', payload: true });
//       const vendorData = {};
//       const pagination = {};

//       for(const vendor of vendorList){
//         if(!mounted) break;
//         try{
//           const res = await fetchVendor(vendor, 1);
//           vendorData[vendor] = res.games;
//           pagination[vendor] = { currentPage: res.pagination.current_page, totalPages: res.pagination.total_pages, loading: false };
//           // update state incrementally so UI can render as vendors come in
//           dispatch({ type: 'SET_VENDOR_DATA', payload: { [vendor]: res.games } });
//           dispatch({ type: 'SET_PAGINATION', payload: { [vendor]: pagination[vendor] } });
//         }catch(err){
//           vendorData[vendor] = [];
//           pagination[vendor] = { currentPage: 1, totalPages: 1, loading: false };
//           dispatch({ type: 'SET_VENDOR_DATA', payload: { [vendor]: [] } });
//           dispatch({ type: 'SET_PAGINATION', payload: { [vendor]: pagination[vendor] } });
//         }
//       }

//       if(mounted) dispatch({ type: 'SET_GAMES_LOADING', payload: false });
//     })();

//     return () => { mounted = false; };
//   }, [fetchVendor]);

//   const handleGameLaunch = useCallback(async (game) => {
//     const isLogged = localStorage.getItem('logged') === 'true';
//     if(!isLogged){ sendNotfication('Please login to play', 'error'); return; }
//     dispatch({ type: 'SET_LOADING', payload: true });
//     try{
//       const coins = localStorage.getItem('coin') || 'USDT';
//       if(!['USDT','INR','BDT'].includes(coins)){
//         sendNotfication('Please Swap Into USDT To Play','error'); return;
//       }
//       const payload = { game_uid: game.game_uid, user_id: getUID(), credit_amount: 100, currency_code: coins, language: 'en' };
//       const result = await gamesService.launchGame(payload);
//       if(result.success && result.data?.game_launch_url){
//         dispatch({ type: 'SET_IFRAME', payload: { show:true, url: result.data.game_launch_url, loading:true } });
//       } else sendNotfication('Game under maintenance','error');
//     }catch(e){
//       dispatch({ type: 'SET_MESSAGE', payload: { type:'error', text:e.message || 'Failed to launch game' } });
//     }finally{
//       dispatch({ type: 'SET_LOADING', payload: false });
//       setTimeout(()=>dispatch({ type: 'SET_MESSAGE', payload:null }), 5000);
//     }
//   }, []);

//   const closeIframe = useCallback(() => dispatch({ type: 'SET_IFRAME', payload: { show:false, url:'', loading:true } }), []);

//   const scrollLeft = (vendor) => scrollRefs.current[vendor]?.current?.scrollBy({ left:-400, behavior:'smooth' });
//   const scrollRight = (vendor) => scrollRefs.current[vendor]?.current?.scrollBy({ left:400, behavior:'smooth' });

//   // Render vendors with Esports only once between the first two groups of three providers
//   const renderVendorsWithEsports = () => {
//     const nodes = [];
//     // Prepare vendors excluding any 'esports' key
//     const filtered = vendorList.filter(v => v !== 'esports');

//     const firstThree = filtered.slice(0, 3);
//     const nextThree = filtered.slice(3, 6);
//     const rest = filtered.slice(6);

//     // Render first 3 providers
//     firstThree.forEach(vendor => {
//       nodes.push(
//         <div key={vendor} style={{ marginTop: 12 }}>
//           <GameSection
//             title={titleFor(vendor)}
//             games={state.vendors[vendor] || []}
//             onGameClick={handleGameLaunch}
//             vendor={vendor}
//             vendorPagination={state.vendorPagination[vendor]}
//             onPagination={() => {}}
//             enabled={true}
//             showMoreState={false}
//             onToggleShowMore={() => {}}
//             scrollContainerRef={scrollRefs.current[vendor]}
//             onScrollLeft={() => scrollLeft(vendor)}
//             onScrollRight={() => scrollRight(vendor)}
//             isMobileView={isMobile()}
//             loading={state.gamesLoading}
//           />
//         </div>
//       );
//     });

//     // Insert Esports after the first 3 providers
//     nodes.push(
//       <div key="esports-insert" style={{ marginTop: 12 }}>
//         <Esports />
//       </div>
//     );

//     // Render next 3 providers
//     nextThree.forEach(vendor => {
//       nodes.push(
//         <div key={vendor} style={{ marginTop: 12 }}>
//           <GameSection
//             title={titleFor(vendor)}
//             games={state.vendors[vendor] || []}
//             onGameClick={handleGameLaunch}
//             vendor={vendor}
//             vendorPagination={state.vendorPagination[vendor]}
//             onPagination={() => {}}
//             enabled={true}
//             showMoreState={false}
//             onToggleShowMore={() => {}}
//             scrollContainerRef={scrollRefs.current[vendor]}
//             onScrollLeft={() => scrollLeft(vendor)}
//             onScrollRight={() => scrollRight(vendor)}
//             isMobileView={isMobile()}
//             loading={state.gamesLoading}
//           />
//         </div>
//       );
//     });

//     // Render the rest of providers (all remaining)
//     rest.forEach(vendor => {
//       nodes.push(
//         <div key={vendor} style={{ marginTop: 12 }}>
//           <GameSection
//             title={titleFor(vendor)}
//             games={state.vendors[vendor] || []}
//             onGameClick={handleGameLaunch}
//             vendor={vendor}
//             vendorPagination={state.vendorPagination[vendor]}
//             onPagination={() => {}}
//             enabled={true}
//             showMoreState={false}
//             onToggleShowMore={() => {}}
//             scrollContainerRef={scrollRefs.current[vendor]}
//             onScrollLeft={() => scrollLeft(vendor)}
//             onScrollRight={() => scrollRight(vendor)}
//             isMobileView={isMobile()}
//             loading={state.gamesLoading}
//           />
//         </div>
//       );
//     });

//     return nodes;
//   };

//   if(state.showIframe){
//     return (
//       <FullScreenContainer>
//         <FullScreenIframe src={state.iframeUrl} title="Game Iframe" />
//         <BackButton onClick={closeIframe}><FontAwesomeIcon icon={faArrowLeft} /></BackButton>
//       </FullScreenContainer>
//     );
//   }

//   return (
//     <>
//       <Helmet>
//         <title>{BRAND} - Crypto Casino Games and Crypto Gambling</title>
//       </Helmet>

//       {state.isLogged ? <SkylaSlider /> : <Banner />}
//       <SportsNavFlipper />
//       {isMobile() ? <MobilePaymentBR /> : <PaymentBar />}

//       {/* Providers + Esports inserted after every 3 providers */}
//       {renderVendorsWithEsports()}

//       {/* Keep GameProviders at the end if you still want it there */}
//       <GameProviders />

//       <CasinoLeaderboard />

//       {state.loading && (
//         <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:9999 }}>
//           <Spinner animation="border" />
//         </div>
//       )}

//       {state.message && (
//         <Alert variant={state.message.type==='error'?'danger':'success'} style={{ position:'fixed', bottom:20, left:'50%', transform:'translateX(-50%)', zIndex:9999 }}>
//           {state.message.text}
//         </Alert>
//       )}
//     </>
//   );
// }

// export default withCookies(HomeFunctional);
